import { Paper, NavLink, ThemeIcon, Badge, ScrollArea, Chip, Group } from "@mantine/core";

/**
 * Smart-list / scope navigation that adapts to width. On desktop it is the familiar vertical
 * NavLink sidebar; on a phone it collapses to a single horizontally-scrollable row of chips, so the
 * list of records below is visible immediately instead of being pushed under a tall full-width nav
 * block (the Tasks hub problem). The page owns the selected scope.
 *
 * `scopes`: [{ value, label, icon?, count? }]. Both variants render (one is CSS-hidden per width),
 * so the selected scope stays in sync without a JS branch.
 */
export default function ScopeNav({ scopes, value, onChange }) {
  return (
    <>
      {/* Desktop: vertical sidebar */}
      <Paper withBorder p={4} radius="md" visibleFrom="sm">
        {scopes.map((s) => (
          <NavLink
            key={s.value}
            active={value === s.value}
            label={s.label}
            leftSection={s.icon ? <ThemeIcon variant="light" size="sm">{s.icon}</ThemeIcon> : undefined}
            rightSection={s.count > 0 ? <Badge size="xs" variant="light">{s.count}</Badge> : undefined}
            onClick={() => onChange(s.value)}
            style={{ borderRadius: "var(--mantine-radius-sm)", marginBottom: 2 }}
          />
        ))}
      </Paper>

      {/* Mobile: horizontal scrollable chip row */}
      <ScrollArea type="never" scrollbars="x" hiddenFrom="sm">
        <Group gap="xs" wrap="nowrap" pb={6}>
          {scopes.map((s) => (
            <Chip
              key={s.value}
              size="sm"
              radius="sm"
              checked={value === s.value}
              onChange={() => onChange(s.value)}
              variant={value === s.value ? "filled" : "outline"}
              style={{ flexShrink: 0 }}
            >
              {s.count > 0 ? `${s.label} (${s.count})` : s.label}
            </Chip>
          ))}
        </Group>
      </ScrollArea>
    </>
  );
}
